import { useTransition, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { NoMessageValues, MessageValue } from 'react-intl';

import { Button } from '@packages/shared/src/ui/Button/Button';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';

import { useDeferredRendering } from '../SearchProductCard/useDeferredRendering';

export type InfiniteLoadingFooterProps = {
  seenProductCount: number;
  totalProductCount: number;
  onLoadMore: () => void;
};

const messages = defineMessages<{
  readonly text: {
    readonly seenProductCount: MessageValue;
    readonly totalProductCount: MessageValue;
  };
  readonly loadMore: NoMessageValues;
}>({
  text: {
    id: 'search.productList.infiniteLoading.footer.text',
    defaultMessage: 'Du hast {seenProductCount} von {totalProductCount} Artikeln gesehen',
  },
  loadMore: {
    id: 'search.productList.infiniteLoading.footer.loadMore',
    defaultMessage: 'Mehr Artikel anzeigen',
  },
});

/**
 * Content of the InfiniteLoadingFooter. Separated to allow deferred hydration.
 */
const InfiniteLoadingFooterContent = ({
  seenProductCount,
  totalProductCount,
  onLoadMore,
}: InfiniteLoadingFooterProps) => {
  const { formatMessage } = useIntl();

  const [isLoading, startTransition] = useTransition();

  return (
    <>
      <span className="text-size-body-sm">
        {formatMessage(messages.text, { seenProductCount, totalProductCount })}
      </span>
      <div className="bg-color-grey mx-2 h-[2px]">
        <div
          className="bg-color-grey-dark h-full"
          style={{ width: `${(seenProductCount / totalProductCount) * 100}%` }}
        />
      </div>
      {seenProductCount < totalProductCount && (
        <Button
          disabled={isLoading}
          onClick={() => {
            startTransition(() => {
              onLoadMore();
            });
          }}
          className={isLoading ? 'animate-pulse' : ''}
        >
          {formatMessage(messages.loadMore)}
        </Button>
      )}
    </>
  );
};

/**
 * Infinite loading footer component with deferred hydration.
 * Renders a placeholder until the component is scrolled into view.
 */
export const InfiniteLoadingFooter = ({
  seenProductCount,
  totalProductCount,
  onLoadMore,
}: InfiniteLoadingFooterProps) => {
  const footerRef = useRef<HTMLDivElement>(null);
  const { showFullContents } = useDeferredRendering({
    ref: footerRef,
    alwaysRender: false,
  });

  return (
    <div
      ref={footerRef}
      className="col-span-full row-[span_1] flex flex-col gap-2 place-self-center"
    >
      {showFullContents ? (
        <InfiniteLoadingFooterContent
          seenProductCount={seenProductCount}
          totalProductCount={totalProductCount}
          onLoadMore={onLoadMore}
        />
      ) : (
        <>
          <Skeleton className="h-5 w-60" />
          <Skeleton className="h-0.5 w-full" />
          <Skeleton className="h-10 w-36 self-center" />
        </>
      )}
    </div>
  );
};
